import { Menu } from '@base-ui/react/menu';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface FilterMultiOption<T extends string> {
  value: T;
  label: string;
  checked: boolean;
  /* Draws a hairline above this option, to set a group apart from the one before. */
  separatorBefore?: boolean;
}

export interface FilterMultiSelectProps<T extends string> {
  /* Sits left of the button, e.g. "Toon". */
  label: string;
  /* Reads the label to screen readers only, for a button that says what it is on its own. */
  hideLabel?: boolean;
  /*
   * What the button reads. The caller works it out, because summarising a set
   * of ticks in one phrase — "alle gesprekken", "2 types" — is copy, and the
   * library holds none.
   */
  buttonLabel: string;
  options: readonly FilterMultiOption<T>[];
  /* One option was pressed. Whether that ticks it, unticks it, or ticks three others is the caller's rule. */
  onSelect: (value: T) => void;
  className?: string;
}

/**
 * The filter for a facet where more than one answer may be true at once.
 *
 * A press never closes the menu. Ticking one category and immediately wanting
 * the next is the ordinary case, and a menu that closes on every tick makes
 * choosing three of five cost three trips. Base UI's checkbox item keeps itself
 * open, and the ticks are what the button summarises afterwards.
 *
 * The sibling of `FilterSelect`, and deliberately not the same component: one
 * answers "which view", the other "which of these", and a single control that
 * did both would have to hide half its behaviour behind a flag.
 */
export function FilterMultiSelect<T extends string>({
  label,
  hideLabel = false,
  buttonLabel,
  options,
  onSelect,
  className,
}: FilterMultiSelectProps<T>) {
  return (
    <div
      data-slot='filter-multi-select'
      className={cn('flex items-center gap-2', className)}
    >
      <span className={hideLabel ? 'sr-only' : 'text-xs font-semibold text-ink-muted'}>
        {label}
      </span>
      <Menu.Root>
        <Menu.Trigger
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-full bg-fill px-3.5 py-2',
            'text-sm font-semibold text-ink transition hover:bg-line',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
          )}
        >
          {buttonLabel}
          <ChevronDown className='size-2.5 stroke-3' aria-hidden='true' />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner side='bottom' align='start' sideOffset={6} className='z-50'>
            {/*
              No padding on the popup and no rounding on the rows: a ticked row
              is a filled row, and an inset pill of fill leaves white wedges
              between two ticked neighbours — which reads as a gap in the set
              rather than as one block of chosen things. The popup keeps its own
              rounded corners and clips the rows to them.
            */}
            <Menu.Popup
              data-slot='filter-select-content'
              aria-label={label}
              className='min-w-52 overflow-hidden rounded-md border border-line bg-surface shadow-menu outline-none animate-pop'
            >
              {options.map((option) => (
                <div key={option.value}>
                  {option.separatorBefore && (
                    <Menu.Separator className='my-1.25 h-px bg-fill' />
                  )}
                  <Menu.CheckboxItem
                    checked={option.checked}
                    onCheckedChange={() => onSelect(option.value)}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2.25',
                      'text-sm font-semibold text-ink outline-none transition-colors',
                      'hover:bg-fill data-highlighted:bg-fill data-checked:bg-fill',
                    )}
                  >
                    {option.label}
                    {/*
                      The tick keeps its room whether or not it is on, so the
                      rows do not shift sideways as the set changes under the
                      pointer.
                    */}
                    <Check
                      className={cn(
                        'size-3.25 stroke-3 text-ink',
                        option.checked ? 'opacity-100' : 'opacity-0',
                      )}
                      aria-hidden='true'
                    />
                  </Menu.CheckboxItem>
                </div>
              ))}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  );
}
